import { postUrl } from '@/apiCalls/fetch';
import { DisplayButtons } from '@/components/DisplayButtons';
import { ELoanUsage, ESellerType } from '@/context/borrower';
import { setNovatedCost } from '@/context/display';
import { EDisplays, EJourneys, setDisplay } from '@/context/display';
import { selectLenders } from '@/context/lender';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { EParameters, getParameters } from '@/utils/setParametersFromParams';

interface IyourScoresProps {
    baseId?: string;
}

export function YourScoresButtons({ baseId }: IyourScoresProps): JSX.Element {
    const dispatch = useAppDispatch();
    const lenders = useAppSelector(selectLenders);
    const params = getParameters();

    // ***** Event Handlers *****
    function handleBackClick(): void {
        const paramJourney = params.get(EParameters.INITIAL_JOURNEY);

        if (paramJourney?.toLocaleUpperCase() === EJourneys.TEST_DRIVE) {
            dispatch(setDisplay(EDisplays.INCOME));
            return;
        }
        dispatch(setDisplay(EDisplays.OCR_SUMMARY));
    }

    async function handleNextClick(): Promise<void> {
        const fetchLoanNumbers = async (): Promise<void> => {
            // Fetch loan numbers
            function formatDecimalValues(data: any): any {
                if (typeof data === 'number') {
                    // Format to 2 decimal places and add commas for thousands
                    return data.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    });
                } else if (data !== null && typeof data === 'object') {
                    if (Array.isArray(data)) {
                        // Handle array
                        return data.map((item) => formatDecimalValues(item));
                    } else {
                        // Handle object
                        const result: Record<string, any> = {};
                        for (const key in data) {
                            if (
                                Object.prototype.hasOwnProperty.call(data, key)
                            ) {
                                result[key] = formatDecimalValues(data[key]);
                            }
                        }
                        return result;
                    }
                }
                // Return non-object, non-number values as is
                return data;
            }
            const state = store.getState();
            const borrower = state.borrowerReducer;
            const seller = state.borrowerReducer.seller;
            const frequencyMultiplier = ((): number => {
                switch (borrower.income.paidFrequency) {
                    case 'WEEKLY':
                        return 52;
                    case 'FORTNIGHTLY':
                        return 26;
                    case 'MONTHLY':
                        return 12;
                    default:
                        return 12;
                }
            })();

            const salary =
                ((borrower.income.paidAmount || 0) +
                    (borrower.income?.otherIncome || 0)) *
                frequencyMultiplier;

            const term = borrower.loan.loanTerm;
            const km = borrower.vehicle.annualKms;
            const hecs =
                borrower.income.hecsDebt !== undefined &&
                borrower.income.hecsDebt > 0;

            const customerState = borrower.address.address[0].state;
            const priceExGst = lenders?.[0]?.financeTotal || 0;
            const body = {
                salary,
                term,
                km,
                state: customerState,
                hecs,
                typeOfCar: 'largeCar',
                isLuxuryCar: false,
                priceExGst,
                isPrivateSale: seller.type === ESellerType.PRIVATE,
                insuranceBudget: 2350,
            };
            const data = await postUrl(`/api/novatedCosts`, body);
            const formattedData = formatDecimalValues(data);
            dispatch(setNovatedCost(formattedData));
        };
        if (
            store.getState().borrowerReducer.loan.loanUsage ===
            ELoanUsage.NOVATED
        ) {
            await fetchLoanNumbers();
        }
        dispatch(setDisplay(EDisplays.INDICATIVELY_APPROVED));
    }

    return (
        <DisplayButtons
            idLeftButton={`${baseId}-button-back`}
            idRightButton={`${baseId}-button-next`}
            rightClick={handleNextClick}
            leftClick={handleBackClick}
        />
    );
}
